/**
 * CustomerLink — Customer 360 Sprint (Day 1)
 *
 * Drop-in clickable replacement for plain-text customer names. Opens the
 * customer profile via CustomerProfileProvider. When called without a
 * customerId (legacy jobs that pre-date the customers collection link),
 * falls back to opening GlobalSearchModal pre-seeded with the name so the
 * user can still navigate to the right record.
 *
 * Three explicit variants (don't invent more):
 *   - inline: underline-on-hover only — drops into existing prose / chip rows
 *   - chip:   bordered rounded pill — for header strips next to tel/sms/email
 *   - pill:   filled gold pill — for primary "open profile" CTAs
 *
 * stopPropagation is on by default — half the call sites are inside clickable
 * parents (scheduler job blocks, deals-list rows, provisioning items). Without
 * it the parent row handler fires simultaneously with the modal open.
 *
 * Usage:
 *   <CustomerLink customerId={customer.id} displayName={customer.name} />
 *   <CustomerLink customerId={job.customerId} displayName={job.client} variant="chip" />
 */

import type { MouseEvent, ReactNode } from 'react';
import { useGlobalSearchContext } from '@/components/search/GlobalSearchProvider';
import { useCustomerProfile } from './CustomerProfileProvider';

type CustomerLinkVariant = 'inline' | 'chip' | 'pill';

interface CustomerLinkProps {
  /** Customer doc id. If absent, falls back to global search pre-seeded with displayName. */
  customerId?: string | null;
  /** Display text. Also used as the global-search seed when customerId is missing. */
  displayName: string;
  /** Visual variant. Default 'inline' — preserves the look of the prior <span>. */
  variant?: CustomerLinkVariant;
  /** Pass-through className appended after the variant's base classes. */
  className?: string;
  /** Render arbitrary children instead of plain displayName text (e.g. wrap with an icon). */
  children?: ReactNode;
  /** Stop propagation so the click doesn't trigger parent row/card handlers. Default: true. */
  stopPropagation?: boolean;
}

// Variant-specific classes. Kept here so they're impossible to drift from the
// JSDoc above. Add a new variant ONLY by editing the union + this map.
const VARIANT_CLASSES: Record<CustomerLinkVariant, string> = {
  inline:
    'inline text-left bg-transparent border-0 p-0 ' +
    'hover:underline underline-offset-2 hover:text-brand-secondary',
  chip:
    'inline-flex items-center gap-1 px-2 py-0.5 rounded-md ' +
    'border border-theme-soft bg-surface-card ' +
    'hover:border-brand-secondary/40 hover:text-brand-secondary text-xs',
  pill:
    'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full ' +
    'bg-brand-secondary/10 border border-brand-secondary/25 text-brand-secondary ' +
    'hover:bg-brand-secondary/15 text-xs font-medium',
};

const BASE_CLASSES =
  'focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-secondary ' +
  'rounded transition-colors cursor-pointer';

export function CustomerLink({
  customerId,
  displayName,
  variant = 'inline',
  className,
  children,
  stopPropagation = true,
}: CustomerLinkProps) {
  const { openCustomer } = useCustomerProfile();
  const search = useGlobalSearchContext();

  // Fix-pass: when there's NO customerId AND NO usable displayName, the
  // global-search fallback would open the modal with an empty query and
  // trap the user. Bail out to a non-interactive span instead.
  const trimmedName = (displayName ?? '').trim();
  if (!customerId && !trimmedName) {
    return (
      <span
        className={`text-theme-muted italic ${className ?? ''}`}
        title="No customer linked"
      >
        Unknown customer
      </span>
    );
  }

  const handleClick = (e: MouseEvent<HTMLButtonElement>) => {
    if (stopPropagation) e.stopPropagation();
    if (customerId) {
      openCustomer(customerId);
    } else if (trimmedName) {
      // Legacy job / fault with no customerId. Open global search pre-seeded
      // with the name so the user can still find and open the right record.
      search.setActiveFilter('customer');
      search.setQuery(trimmedName);
      search.open();
    }
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      title={`Open ${trimmedName || 'customer'} profile`}
      aria-label={`Open ${trimmedName || 'customer'} profile`}
      // Concatenation order: caller `className` first, then variant classes
      // LAST so the variant's hover utility (e.g. `hover:text-brand-secondary`)
      // is the later-declared rule in the className string. CSS cascade
      // resolves utilities by stylesheet order (not className order), but
      // putting variant last also rescues the case where Tailwind chunking
      // emits the caller's color utility after the variant — without this,
      // hover affordance could silently disappear.
      className={`${BASE_CLASSES} ${className ?? ''} ${VARIANT_CLASSES[variant]}`}
    >
      {children ?? trimmedName}
    </button>
  );
}
